/**
 * reCAPTCHA Enterprise account defender: a token for the `user/login` body, which player-backend assesses
 * report-only. Never blocks or noticeably slows a login — any failure or timeout omits the field.
 */

export type AccountDefenderAction = 'login' | 'signup';

export interface AccountDefenderRecaptcha {
  token: string;
  platform: 'web';
  action: AccountDefenderAction;
}

export const ACCOUNT_DEFENDER_RECAPTCHA_TIMEOUT_MS = 2000;

interface GrecaptchaEnterprise {
  ready(callback: () => void): void;
  /** html/index.html loads enterprise.js with `render=` this key, so it executes by site key directly. */
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

const grecaptchaEnterprise = () =>
  (window as unknown as { grecaptcha?: { enterprise?: GrecaptchaEnterprise } }).grecaptcha?.enterprise;

const execute = (enterprise: GrecaptchaEnterprise, siteKey: string, action: AccountDefenderAction) =>
  new Promise<string>((resolve, reject) => {
    enterprise.ready(() => {
      enterprise.execute(siteKey, { action }).then(resolve, reject);
    });
  });

export async function getAccountDefenderRecaptcha(
  action: AccountDefenderAction,
  siteKey: string | undefined = import.meta.env.VITE_RECAPTCHA_AUTH_SITE_KEY,
  timeoutMs: number = ACCOUNT_DEFENDER_RECAPTCHA_TIMEOUT_MS,
): Promise<AccountDefenderRecaptcha | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const enterprise = grecaptchaEnterprise();
    if (!siteKey || !enterprise) return undefined;

    const token = await Promise.race([
      execute(enterprise, siteKey, action),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), timeoutMs);
      }),
    ]);
    return typeof token === 'string' && token.length > 0 ? { token, platform: 'web', action } : undefined;
  } catch {
    // Script blocked, key misconfigured, Google down: the login goes ahead without the field.
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}
